import { useCallback } from 'react';
import { useForm } from 'react-hook-form';
import {
    Link,
    useNavigate,
} from 'react-router-dom';
import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation } from '@tanstack/react-query';
import {
    ArrowLeft,
    Mail,
} from 'lucide-react';
import { toast } from 'sonner';
import { z } from 'zod';

import { Button } from '@/components/ui/button';
import {
    Card,
    CardContent,
} from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { baseRequest } from '@/lib/base';
import { cn } from '@/lib/utils';

const forgotPasswordSchema = z.object({
    email: z.string().min(1, 'Email is required').email('Invalid email address'),
});

type ForgotPasswordFormData = z.infer<typeof forgotPasswordSchema>

const sendForgotPasswordOTP = async (email: string) => {
    const response = await baseRequest({
        url: '/system/auth/forgot-password/',
        method: 'POST',
        body: JSON.stringify({ email }),
        contentType: 'application/json',
    });
    return response;
};

export default function ForgotPassword() {
    const navigate = useNavigate();
    const {
        register,
        handleSubmit,
        formState: { errors },
    } = useForm<ForgotPasswordFormData>({
        resolver: zodResolver(forgotPasswordSchema),
    });

    const forgotPasswordMutation = useMutation({
        mutationFn: (email: string) => sendForgotPasswordOTP(email),
        onSuccess: (response, email) => {
            if (response.ok) {
                toast.success(response.data?.detail || 'OTP sent to your email');
                navigate('/verify-otp', { state: { email, flow: 'forgot-password' } });
            } else {
                toast.error(response.data?.detail || 'Failed to send OTP. Please try again.');
            }
        },
        onError: () => {
            toast.error('Something went wrong. Please try again.');
        },
    });

    const onSubmit = useCallback((data: ForgotPasswordFormData) => {
        forgotPasswordMutation.mutate(data.email);
    }, [forgotPasswordMutation]);

    return (
        <div className="min-h-screen bg-background flex flex-col">
            {/* Top nav */}
            <div className="p-6">
                <Link
                    to="/login"
                    className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground transition-colors"
                >
                    <ArrowLeft className="h-4 w-4" />
                    Go Back
                </Link>
            </div>

            <div className="flex-1 flex items-center justify-center px-4 pb-12">
                <div className="flex flex-col gap-6 w-full max-w-sm">
                    {/* Logo and header */}
                    <div className="flex flex-col items-center gap-3">
                        <Link to="/">
                            <img src="/chanco-vertical.png" alt="Chanco" className="h-24 w-auto" />
                        </Link>
                        <h1 className="text-2xl font-bold text-foreground">Forgot Password</h1>
                        <p className="text-sm text-muted-foreground text-center">
                            Enter your email address and we&apos;ll
                            send you an OTP to reset your password
                        </p>
                    </div>

                    <Card className="border-0 shadow-lg">
                        <CardContent className="pt-6">
                            <form onSubmit={handleSubmit(onSubmit)}>
                                <div className="grid gap-5">
                                    {/* Email field */}
                                    <div className="grid gap-2">
                                        <Label htmlFor="email">Email</Label>
                                        <div className="relative">
                                            <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                                            <Input
                                                id="email"
                                                type="text"
                                                placeholder="m@example.com"
                                                {...register('email')}
                                                className={cn(
                                                    'pl-10',
                                                    { 'focus-visible:ring-destructive': errors.email },
                                                )}
                                            />
                                        </div>
                                        {errors.email && <span className="text-sm text-destructive">{errors.email.message}</span>}
                                    </div>

                                    {/* Send OTP button */}
                                    <Button
                                        type="submit"
                                        className="w-full"
                                        disabled={forgotPasswordMutation.isPending}
                                    >
                                        {forgotPasswordMutation.isPending ? 'Sending...' : 'Send OTP'}
                                    </Button>
                                </div>
                            </form>
                        </CardContent>
                    </Card>
                </div>
            </div>
        </div>
    );
}
